import * as React from 'react'
import { Focusable } from './Focusable'
import { classNames } from './helpers'
import type { MediaType } from '../api-types'

/**
 * What the toggle needs to know about one title in one list.
 *
 * Structurally the app shell's `ListMembership` (see the portal's `src/lists/`), but
 * declared here rather than imported: the styleguide is a leaf — pilets and the shell
 * both depend on it and it depends on neither — so the state is **injected** by the
 * consuming pilet, exactly as `createPersistedState` used to be and as the router
 * `Link` still is for {@link Hero}.
 *
 * The state itself lives in one place, the shell, because a saved list is now account
 * data on the server (`/api/3.0/profile/list…`): every toggle for a title reads the
 * same manifest and they flip together.
 */
export interface ListToggleMembership {
  /** Is the title in the list? Optimistic — flips before the write settles. */
  active: boolean;
  /** Has the membership manifest landed? */
  ready: boolean;
  /** Can this viewer save at all? False when signed out — the toggle then hides. */
  available: boolean;
  /** Is a write for this title in flight? Rendered as `aria-busy`. */
  pending: boolean;
  /** Flip it. Optimistic, rolled back on failure, and never rejects. */
  toggle (): Promise<void> | void;
}

export interface ListToggleLabels {
  /** aria-label / title when the item is already in the list. */
  activeLabel: string;
  /** aria-label / title when the item is not yet in the list. */
  inactiveLabel: string;
}

export interface ListToggleConfig {
  /**
   * Injected hook resolving this title's membership — in practice
   * `movieId => app.useListMembership('favorites', movieId)`. Called once per render,
   * so it must obey the rules of hooks.
   */
  useMembership: (movieId: string) => ListToggleMembership;
  /** Icon shown in both states — the active state is conveyed by the red border. */
  icon: React.ReactNode;
  /**
   * Injected hook resolving the accessible labels for each state. Injected rather
   * than imported (like {@link ListToggleConfig.useMembership}) so the styleguide
   * stays i18n-free: the pilet wires this to `useTranslation`, so the labels follow
   * the active language reactively. Called once per render, so it must obey the
   * rules of hooks.
   */
  useLabels: () => ListToggleLabels;
  /** Extra class on the compact (in-tile) button, e.g. a positional offset. */
  className?: string;
  /** Extra class on the `full` (hero) button. */
  fullClassName?: string;
}

export interface ListToggleProps {
  movieId: string;
  /**
   * The title's type, as the extension slot passes it. The list API resolves the
   * type server-side from the catalogue, so the toggle no longer stores it — it is
   * kept on the props because the slot's other consumers still supply it.
   */
  media_type?: MediaType;
  /** Hero variant: pill-styled `.Button` sized to sit beside "Watch Now". */
  full?: boolean;
}

/**
 * Builds a per-list toggle button (favorites, watch-later, …). The two lists are
 * behaviourally identical — flip one title in or out of one server-side list — so the
 * logic lives here once and each pilet supplies only its membership hook, icon and
 * labels.
 *
 * Call this once per pilet, in `setup(app)`, where the pilet API is in scope:
 *
 * ```tsx
 * import { useTranslation } from 'react-i18next'
 * import { createListToggle } from '@medialib/styleguide'
 *
 * export const createFavoriteToggle = (app: PiletApi) => createListToggle({
 *   useMembership: movieId => app.useListMembership('favorites', movieId),
 *   icon: <i className='fa-solid fa-heart' />,
 *   useLabels: () => {
 *     const { t } = useTranslation('favorites')
 *     return { activeLabel: t('toggle.remove'), inactiveLabel: t('toggle.add') }
 *   },
 * })
 * ```
 */
export function createListToggle (config: ListToggleConfig): React.FC<ListToggleProps> {
  return function ListToggle (props: ListToggleProps) {
    // Both injected hooks are called unconditionally, above the early return, to
    // satisfy the rules of hooks.
    const membership = config.useMembership(props.movieId)
    const { activeLabel, inactiveLabel } = config.useLabels()

    const onClick = React.useCallback((event: React.MouseEvent) => {
      event.preventDefault()
      // The shell's toggle is optimistic and never rejects — it flips the shared
      // manifest, writes, and rolls back itself — so there is nothing to handle here
      // and nothing that may escape into the click handler.
      membership.toggle()
    }, [membership])

    // Signed out, or a manifest we failed to read: there is no local fallback to save
    // to any more, so the toggle renders nothing rather than offering a write that
    // cannot land.
    if (!membership.available) return <></>

    const label = membership.active ? activeLabel : inactiveLabel

    // Compact variant is the circular in-tile toggle; `full` is the hero pill
    // (icon only — the accessible name comes from aria-label, title gives the tooltip).
    const className = props.full
      ? classNames('mlsg-hero-button', 'mlsg-list-toggle--full', config.fullClassName)
      : classNames('mlsg-list-toggle', config.className)

    const button = (
      <button
        type='button'
        onClick={onClick}
        aria-pressed={membership.active}
        aria-label={label}
        title={label}
        // The write is optimistic, so the pressed state is already correct; `aria-busy`
        // says the round trip behind it has not settled. The button stays enabled —
        // disabling it would drop focus mid-interaction on a remote, and the shell
        // ignores a second flip while the first is in flight anyway.
        aria-busy={membership.pending || undefined}
        data-toggled={`${membership.active}`}
        className={className}
      >
        <div>{config.icon}</div>
      </button>
    )

    // Each hero toggle is its OWN spatial stop. The hero renders these through an
    // extension slot, so several toggles (favorites, watch-later) arrive as one
    // component — wrapping that slot from the outside would collapse them into a
    // single stop and strand every toggle but the first.
    //
    // Only the `full` (hero) variant registers. The compact variant lives inside a
    // tile, which is already a stop: a nested stop there would make arrow keys
    // fight over the tile and its own toggle.
    return props.full
      ? <Focusable className='mlsg-focusable--inline'>{button}</Focusable>
      : button
  }
}
